import { type ArrayType, assert, createInterval, executeOnce, isArrayNotEmpty, normalize } from '../../../utils'

import {
    Capability,
    ConnectwareError,
    type ConnectwareErrorExtras,
    ConnectwareErrorType,
    type CybusPermission,
    type CybusRole,
    type CybusUser,
    CybusUserAuthenticationMethod,
    type PaginatedData,
    type RoleCreationRequest,
    type RoleEditingRequest,
    Translation,
    type UserCreationRequest,
    type UserEditingRequest,
    type UserManagementConfiguration,
} from '../../../domain'

import type { PasswordChangeRequest, TranslationService, UserService } from '../../../application'

import type { BackendJsonResponseContent } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, extractValidatedJson } from '../Base'
import { ConnectwareHTTPCapabilitiesService } from './Capabilities'
import {
    mapRoleCreationRequestToPostRequest,
    mapRoleEditingRequestToPostRequest,
    mapUserCreationRequestToUserPostRequest,
    type MapUserDataConfiguration,
    mapUserEditingRequestToUserPutRequest,
    type PaginationConfiguration,
    UserManagementMapper,
} from './mappers'
import { PoliciesValidator } from './PoliciesValidator'
import type { MinimalPaginatedPermissionsResponse, MinimalPaginatedRoles, MinimalPaginatedUsers, MinimalPermissionIndex, MinimalRoleIndex } from './Types'
import { PaginatedFetchHelper } from './UserPaginationHelper'
import {
    isMinimalPaginatedPermissionsResponse,
    isMinimalPaginatedRoles,
    isMinimalPaginatedUsers,
    isMinimalPermissionIndex,
    isMinimalRoleIndex,
    isMinimalUserIndex,
} from './Validators'

type Config = Readonly<{ usersConfig: MapUserDataConfiguration, pagination: PaginationConfiguration, cacheInterval: number }>

export const LDAP_GROUP_MODE = 'GROUP'

export class ConnectwareHTTPUserService extends ConnectwareHTTPCapabilitiesService implements UserService {
    private readonly fetchUsernamePolicy = executeOnce(
        () =>
            this.request<'/api/policy/username', 'GET', BackendJsonResponseContent<'/api/policy/username'>>({
                capability: Capability.RETRIEVE_USERNAME_POLICY,
                method: 'GET',
                path: '/api/policy/username',
                authenticate: true,
                handlers: {
                    200: (r) => r.getJson(),
                    503: () => new ConnectwareError(ConnectwareErrorType.SERVER_NOT_AVAILABLE, 'Network error when reaching server'),
                },
            }),
        true
    )

    private readonly fetchPasswordPolicy = executeOnce(
        () =>
            this.request<'/api/policy/password', 'GET', BackendJsonResponseContent<'/api/policy/password'>>({
                capability: Capability.RETRIEVE_PASSWORD_POLICY,
                method: 'GET',
                path: '/api/policy/password',
                authenticate: true,
                handlers: {
                    200: (r) => r.getJson(),
                    503: () => new ConnectwareError(ConnectwareErrorType.SERVER_NOT_AVAILABLE, 'Network error when reaching server'),
                },
            }),
        true
    )

    private readonly fetchUsernamesIndex = executeOnce(
        () =>
            this.request<'/api/users/usernames', 'GET', string[]>({
                capability: Capability.MANAGE_USERS,
                method: 'GET',
                path: '/api/users/usernames',
                authenticate: true,
                handlers: { 200: (r) => extractValidatedJson(r, isMinimalUserIndex).then((data) => data.map((u) => u.username)) },
            }),
        true
    )

    private readonly fetchRoleNamesIndex = executeOnce(
        () =>
            this.request<'/api/roles/names', 'GET', MinimalRoleIndex>({
                capability: Capability.MANAGE_ROLES,
                method: 'GET',
                path: '/api/roles/names',
                authenticate: true,
                /** Filters out auto generated names which are internal roles */
                handlers: {
                    200: (r) =>
                        extractValidatedJson(r, isMinimalRoleIndex).then((roles) => roles.filter((r) => !/^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$/.test(r.name))),
                    401: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authentication Error while fetching roles index'),
                    403: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authorization Error while fetching roles index'),
                },
            }),
        true
    )

    private readonly fetchPermissionsIndex = executeOnce(
        () =>
            this.request<'/api/permissions/ids', 'GET', MinimalPermissionIndex>({
                capability: Capability.MANAGE_PERMISSIONS,
                method: 'GET',
                path: '/api/permissions/ids',
                authenticate: true,
                handlers: {
                    200: (r) => extractValidatedJson(r, isMinimalPermissionIndex),
                    401: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authentication Error while fetching permissions index'),
                },
            }),
        true
    )

    private readonly fetchMfaSetupStatus = executeOnce(() =>
        this.request<'/api/auth/mfa', 'GET', boolean>({
            capability: Capability.USER_MANAGEMENT_CONFIGURATION,
            method: 'GET',
            path: '/api/auth/mfa',
            authenticate: true,
            handlers: {
                200: (response) => response.getJson().then(({ enabled }) => enabled),
                403: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Could not fetch MFA status due to bad authentication'),
                503: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Could not fetch MFA status due to server error'),
            },
        })
    )

    /**
     * Fetches configuration which might affect user and role setup:
     * - LDAP
     * - MS Entra ID
     * - MFA
     */
    fetchManagementConfiguration: () => Promise<UserManagementConfiguration> = executeOnce(async () => {
        const [isLdapEnabled, isEntraEnabled, isMfaEnabled] = await Promise.all([
            this.request<'/api/auth/ldap', 'GET', boolean>({
                capability: Capability.USER_MANAGEMENT_CONFIGURATION,
                method: 'GET',
                path: '/api/auth/ldap',
                authenticate: true,
                handlers: {
                    200: async (response) => {
                        const { enabled, mode } = await response.getJson()
                        return enabled && mode === LDAP_GROUP_MODE
                    },
                },
            }),
            this.fetchEntraStatus(),
            this.fetchMfaSetupStatus(),
        ])

        return { isLdapEnabled, isEntraEnabled, isMfaEnabled }
    }, true)

    private readonly validator: PoliciesValidator

    private readonly roleFetcher: PaginatedFetchHelper<ArrayType<MinimalRoleIndex>, MinimalPaginatedRoles, boolean, CybusRole>

    private readonly userFetcher: PaginatedFetchHelper<string, MinimalPaginatedUsers, boolean, CybusUser>

    private readonly permissionFetcher: PaginatedFetchHelper<ArrayType<MinimalPermissionIndex>, MinimalPaginatedPermissionsResponse, void, CybusPermission>

    private static createErrorHandler (...params: ConstructorParameters<typeof ConnectwareError>): () => Promise<never> {
        return () => Promise.reject(new ConnectwareError(...params))
    }

    private static createBadRequestHandler (action: string, extras?: ConnectwareErrorExtras<ConnectwareError>): () => Promise<never> {
        return this.createErrorHandler(ConnectwareErrorType.BAD_REQUEST, `Unexpected bad request detected while ${action}`, extras)
    }

    private static createAuthenticationErrorHandler (message: string): () => Promise<never> {
        return this.createErrorHandler(ConnectwareErrorType.AUTHENTICATION, message)
    }

    private static createNotFoundHandler (message: string, extras: ConnectwareErrorExtras<ConnectwareError>): () => Promise<never> {
        return this.createErrorHandler(ConnectwareErrorType.NOT_FOUND, message, extras)
    }

    constructor (options: ConnectwareHTTPServiceOptions, private readonly config: Config, private readonly translationService: TranslationService) {
        super(options)

        const { usersConfig, pagination, cacheInterval } = config

        this.validator = new PoliciesValidator(translationService)

        const responseMapper = new UserManagementMapper(usersConfig)

        this.roleFetcher = new PaginatedFetchHelper<ArrayType<MinimalRoleIndex>, MinimalPaginatedRoles, boolean, CybusRole>({
            pagination,
            requestIndex: () => this.fetchRoleNamesIndex(),
            requestPage: ({ pageNumber, pageSize, showInternal, matches }) =>
                this.request<'/api/roles/page', 'GET', MinimalPaginatedRoles>({
                    capability: Capability.MANAGE_ROLES,
                    method: 'GET',
                    path: '/api/roles/page',
                    authenticate: true,
                    queryParams: {
                        isShared: 'true',
                        rowsPerPage: `${pageSize}`,
                        pageNumber: `${pageNumber}`,
                        ...(showInternal === null || showInternal ? {} : { autoGenerated: 'false' }),
                        ...(matches ? { names: matches.map((m) => m.name) } : {}),
                    },
                    handlers: {
                        200: (r) => extractValidatedJson(r, isMinimalPaginatedRoles),
                        400: ConnectwareHTTPUserService.createBadRequestHandler('loading roles page', { pageNumber, pageSize, showInternal, matches }),
                        401: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authentication Error while fetching roles page'),
                        403: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authorization Error while fetching roles page'),
                    },
                }),
            requestMetadata: () => this.fetchEntraStatus(),
            mapDataToPage: (response, isEntraEnabled) => responseMapper.mapRolesPage(response, isEntraEnabled),
            doesIndexMatch: ({ name }, { isExact, raw, normalized }) => (isExact ? name === raw : normalize(name).includes(normalized)),
        })

        this.userFetcher = new PaginatedFetchHelper<string, MinimalPaginatedUsers, boolean, CybusUser>({
            pagination,
            requestIndex: () => this.fetchUsernamesIndex(),
            requestPage: ({ pageNumber, pageSize, showInternal, matches }) =>
                this.request<'/api/listUsers', 'GET', MinimalPaginatedUsers>({
                    capability: Capability.MANAGE_USERS,
                    method: 'GET',
                    path: '/api/listUsers',
                    queryParams: {
                        ...(showInternal === null ? {} : { excludeAutoGenerated: showInternal ? 'false' : 'true' }),
                        ...(matches ? { usernameEq: [...matches] } : {}),
                        rowsPerPage: `${pageSize}`,
                        pageNumber: `${pageNumber}`,
                    },
                    authenticate: true,
                    handlers: {
                        200: (r) => extractValidatedJson(r, isMinimalPaginatedUsers),
                        400: ConnectwareHTTPUserService.createBadRequestHandler('loading users page', { pageNumber, pageSize, showInternal, matches }),
                        401: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authentication Error while fetching users page'),
                        500: ConnectwareHTTPUserService.createErrorHandler(ConnectwareErrorType.SERVER_ERROR, 'Could not fetch users page', {
                            pageNumber,
                            pageSize,
                            showInternal,
                            matches,
                        }),
                    },
                }),
            requestMetadata: () => this.fetchMfaSetupStatus(),
            mapDataToPage: (response, isMfaSetup) => responseMapper.mapUsersPage(response, isMfaSetup),
            doesIndexMatch: (index, { isExact, raw, normalized }) => (isExact ? index === raw : normalize(index).includes(normalized)),
        })

        this.permissionFetcher = new PaginatedFetchHelper<ArrayType<MinimalPermissionIndex>, MinimalPaginatedPermissionsResponse, void, CybusPermission>({
            pagination,
            requestIndex: () => this.fetchPermissionsIndex(),
            shouldRequestIndex: ({ showInternal }) => showInternal === false,
            requestPage: ({ pageNumber, pageSize, matches }) => {
                return this.request<'/api/permissions/page', 'GET', MinimalPaginatedPermissionsResponse>({
                    capability: Capability.MANAGE_PERMISSIONS,
                    method: 'GET',
                    path: '/api/permissions/page',
                    queryParams: {
                        rowsPerPage: `${pageSize}`,
                        pageNumber: `${pageNumber}`,
                        ids: matches ? matches.flatMap(({ ids }) => ids) : [],
                    },
                    authenticate: true,
                    handlers: {
                        200: (r) => extractValidatedJson(r, isMinimalPaginatedPermissionsResponse),
                        400: ConnectwareHTTPUserService.createBadRequestHandler('loading permissions page', { pageNumber, pageSize, matches }),
                        401: ConnectwareHTTPUserService.createAuthenticationErrorHandler('Authorization issues encountered while fetching permissions'),
                    },
                })
            },
            requestMetadata: () => Promise.resolve(),
            mapDataToPage: (response) => responseMapper.mapPermissionsPage(response),
            doesIndexMatch: ({ context, operation, resource }, { normalized, showInternal }) => {
                if (!showInternal && (context === 'control_plane' || resource.startsWith('/api/'))) {
                    /**
                     * Does not wants to show internal but it's an internal API, drop it
                     */
                    return false
                }

                if (
                    normalized &&
                    !normalize(context).includes(normalized) &&
                    !normalize(operation).includes(normalized) &&
                    !normalize(resource).includes(normalized)
                ) {
                    /**
                     * Has search but no match
                     */
                    return false
                }

                return true
            },
        })

        createInterval(() => this.clearUsersCache(), cacheInterval)
    }

    private async ensureNameUsage (
        entityByNamePromise: Promise<{ id: string }>,
        id: string | null,
        ...translate: Parameters<TranslationService['translate']>
    ): Promise<void> {
        const entityByNameOrError = await entityByNamePromise.catch((e: ConnectwareError) => e)

        if (
            /** The entity should not exist while updating or creating, and the BE error dictates so, all good */
            ConnectwareError.isOfTypes(entityByNameOrError, ConnectwareErrorType.NOT_FOUND) ||
            /** The entity should exist, and its id equals the current one, all good */
            (id !== null && !ConnectwareError.is(entityByNameOrError) && entityByNameOrError.id === id)
        ) {
            return
        }

        /** Either propagate issues with the current promise or throw the usage error we found */
        throw ConnectwareError.is(entityByNameOrError)
            ? entityByNameOrError
            : new ConnectwareError(ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION, this.translationService.translate(...translate))
    }

    private ensureUsernameUsage (id: CybusUser['id'] | null, username: CybusUser['username']): Promise<void> {
        return this.ensureNameUsage(this.fetchUser(username), id, Translation.USERNAME_ALREADY_USED, { username })
    }

    private ensureRoleNameUsage (id: CybusRole['id'] | null, name: CybusRole['name']): Promise<void> {
        return this.ensureNameUsage(this.fetchRole(name), id, Translation.ROLE_NAME_ALREADY_USED, { name })
    }

    protected clearUsersCache (): void {
        this.fetchUsernamesIndex.clear()
        this.fetchRoleNamesIndex.clear()
        this.fetchPermissionsIndex.clear()
    }

    changePassword (body: PasswordChangeRequest): Promise<void> {
        return this.request<'/api/users/change-password', 'PUT', void>({
            capability: Capability.MANAGE_OWN_PASSWORD,
            method: 'PUT',
            path: '/api/users/change-password',
            authenticate: true,
            body,
            handlers: {
                204: () => Promise.resolve(),
                400: ConnectwareHTTPUserService.createBadRequestHandler('changing password'),
                403: ConnectwareHTTPUserService.createAuthenticationErrorHandler('There were issues while authenticating the user'),
            },
        })
    }

    validatePassword (password: string): Promise<readonly (string | ConnectwareError)[]> {
        return this.validator.validatePassword(this.fetchPasswordPolicy(), password)
    }

    validateUsername (username: string): Promise<readonly (string | ConnectwareError)[]> {
        return this.validator.validateUsername(this.fetchUsernamePolicy(), username)
    }

    fetchRoles (search: string): Promise<readonly CybusRole[]> {
        return this.roleFetcher.fetch(search)
    }

    fetchRolesPage (search: string, showInternal: boolean, pageNumber: number): Promise<PaginatedData<CybusRole>> {
        return this.roleFetcher.fetchPage(search, showInternal, pageNumber)
    }

    fetchRole (roleName: CybusRole['name']): Promise<CybusRole> {
        return this.roleFetcher.fetchOne(roleName)
    }

    fetchUsers (search: string): Promise<readonly CybusUser[]> {
        return this.userFetcher.fetch(search)
    }

    fetchUsersPage (search: string, showInternal: boolean, pageNumber: number): Promise<PaginatedData<CybusUser>> {
        return this.userFetcher.fetchPage(search, showInternal, pageNumber)
    }

    fetchUser (username: CybusUser['username']): Promise<CybusUser> {
        return this.userFetcher.fetchOne(username)
    }

    fetchPermissionsPage (search: string, showInternal: boolean, pageNumber: number): Promise<PaginatedData<CybusPermission>> {
        return this.permissionFetcher.fetchPage(search, showInternal, pageNumber)
    }

    async createUser (request: UserCreationRequest): Promise<void> {
        await this.ensureUsernameUsage(null, request.username)

        await this.request<'/api/users/batch', 'POST', void>({
            capability: Capability.MANAGE_USERS,
            method: 'POST',
            path: '/api/users/batch',
            authenticate: true,
            body: [mapUserCreationRequestToUserPostRequest(request)],
            handlers: {
                201: () => Promise.resolve(),
                400: ConnectwareHTTPUserService.createBadRequestHandler('creating user', { username: request.username }),
            },
        })

        this.clearUsersCache()
    }

    async updateUser ({ id, ...request }: UserEditingRequest): Promise<void> {
        // check if the user is protected and if the authentication method "TOKEN" is part of the request
        if (
            request.username &&
            this.config.usersConfig.protectedUsers.has(request.username) &&
            !request.authenticationMethods?.includes(CybusUserAuthenticationMethod.TOKEN)
        ) {
            throw new ConnectwareError(
                ConnectwareErrorType.BAD_REQUEST,
                this.translationService.translate(Translation.USER_UPDATE_ERROR_PROTECTED_USER, {
                    method: this.translationService.translate(Translation.USER_AUTHENTICATION_METHOD, { method: CybusUserAuthenticationMethod.TOKEN }),
                    username: request.username,
                })
            )
        }

        if (request.username !== undefined) {
            await this.ensureUsernameUsage(id, request.username)
        }

        await this.request<'/api/users/+', 'PUT', void>({
            capability: Capability.MANAGE_USERS,
            method: 'PUT',
            path: '/api/users/+',
            pathParams: [id],
            authenticate: true,
            body: mapUserEditingRequestToUserPutRequest(request),
            handlers: {
                200: () => Promise.resolve(),
                400: ConnectwareHTTPUserService.createBadRequestHandler('updating user', { id }),
                404: ConnectwareHTTPUserService.createNotFoundHandler('User not found', { id }),
            },
        })

        this.clearUsersCache()
    }

    async deleteUser (id: CybusUser['id']): Promise<void> {
        await this.request<'/api/users/batch/delete', 'POST', void>({
            capability: Capability.MANAGE_USERS,
            method: 'POST',
            path: '/api/users/batch/delete',
            authenticate: true,
            body: [id],
            handlers: {
                200: () => Promise.resolve(),
                400: ConnectwareHTTPUserService.createBadRequestHandler('deleting user', { id }),
            },
        })

        this.clearUsersCache()
    }

    async createRole (request: RoleCreationRequest): Promise<void> {
        await this.ensureRoleNameUsage(null, request.name)

        await this.request<'/api/roles', 'POST', void>({
            capability: Capability.MANAGE_ROLES,
            method: 'POST',
            path: '/api/roles',
            authenticate: true,
            body: mapRoleCreationRequestToPostRequest(request),
            handlers: {
                201: () => Promise.resolve(),
                400: ConnectwareHTTPUserService.createBadRequestHandler('creating role', { request }),
            },
        })

        this.clearUsersCache()
    }

    async updateRole ({ id, ...request }: RoleEditingRequest): Promise<void> {
        const body = mapRoleEditingRequestToPostRequest(request)
        await this.ensureRoleNameUsage(id, request.name)

        await this.request<'/api/roles/+', 'PUT', void>({
            capability: Capability.MANAGE_ROLES,
            method: 'PUT',
            path: '/api/roles/+',
            pathParams: [id],
            authenticate: true,
            body,
            handlers: {
                200: () => Promise.resolve(),
                400: ConnectwareHTTPUserService.createBadRequestHandler('updating role', { id, body }),
                404: ConnectwareHTTPUserService.createNotFoundHandler('Role not found', { id }),
            },
        })

        this.clearUsersCache()
    }

    async deleteRole (id: CybusRole['id']): Promise<void> {
        const name = await this.fetchRoleNamesIndex().then((roles) => roles.find((r) => r.id === id)?.name)

        assert(
            name,
            /** Should only happen in inconsistent scenarios where the role is somehow gone from the BE */
            new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Could not find role by name', { id })
        )

        const { users } = await this.roleFetcher.fetchOne(name)

        if (isArrayNotEmpty(users)) {
            /** There are users with this role still */
            throw new ConnectwareError(
                ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION,
                this.translationService.translate(Translation.IS_USED_BY, {
                    used: this.translationService.translate(Translation.ROLE, { count: 1 }),
                    user: this.translationService.list(users),
                })
            )
        }

        await this.request<'/api/roles/+', 'DELETE', void>({
            capability: Capability.MANAGE_ROLES,
            method: 'DELETE',
            path: '/api/roles/+',
            pathParams: [id],
            authenticate: true,
            handlers: {
                200: () => Promise.resolve(),
                404: ConnectwareHTTPUserService.createNotFoundHandler('Role not found', { id }),
                409: ConnectwareHTTPUserService.createErrorHandler(ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION, 'Role is protected', { id }),
            },
        })

        this.clearUsersCache()
    }
}
